/**
 * Thumbnails `opt/` — implementación ÚNICA.
 *
 * Antes esto estaba duplicado en 8 archivos con canvas inline y parámetros ya
 * divergentes. Los de imágenes GENERADAS se movieron al backend (lib/thumbnail.ts, con
 * ffmpeg) porque dependían de que un browser hubiera mirado la imagen: el Editor IA no
 * pasa por GeneratedImagesInline y tenía 0 de 524 thumbnails.
 *
 * Los de PRODUCTO siguen acá: las fotos van del browser directo a Storage, el backend
 * no tiene endpoint de subida.
 */

/**
 * Lado largo del thumbnail. Antes 300px, que se veía pixelado en desktop: las grillas
 * son de 3-4 columnas con cards de ~285-380 CSS px, y en retina (DPR 2) eso pide
 * 570-760px REALES. Mismo valor que OPT_MAX_SIDE del backend — si cambia uno, cambiar
 * el otro o los thumbnails quedan de dos tamaños distintos.
 */
export const OPT_MAX_SIDE = 800;

const JPEG_QUALITY = 0.82;

/**
 * Genera la versión optimizada de una imagen: encoge el lado largo a `maxSide`
 * conservando el aspecto, y NO agranda si ya es más chica.
 *
 * BEST-EFFORT: devuelve `null` en vez de lanzar (imagen corrupta, canvas no disponible,
 * toBlob que falla). El llamador decide, pero un thumbnail nunca debe cortar un upload.
 */
export async function buildThumbnailBlob(
  source: Blob,
  maxSide: number = OPT_MAX_SIDE,
): Promise<Blob | null> {
  const url = URL.createObjectURL(source);
  try {
    return await new Promise<Blob | null>((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          // Sólo se reduce: una foto ya chica se deja como está en vez de interpolarla.
          const factor = Math.min(1, maxSide / Math.max(width, height));
          width = Math.max(1, Math.round(width * factor));
          height = Math.max(1, Math.round(height * factor));

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY);
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
